import { createStore } from "./store.js";
import { attachStoreDevtools } from "./devtools.js";

// Filters sentinels (the "no filter" state): empty tracks = ALL tracks (not none),
// null level/tag = any, empty search = off. matchesFilters in selectors.js enforces this.
export const emptyFilters = { tracks: [], level: null, tag: null, search: "" };

export const initialState = {
  status: "idle",
  error: null,
  selectedDay: 1,
  sessions: [],
  tracks: [],
  rooms: [],
  speakers: [],
  conference: null,
  favorites: new Set(),
  filters: emptyFilters,
};

// Always returns a NEW state object (immutable updates), so subscribers and
// persistence can key off identity changes instead of deep-comparing.
export function reducer(state, action) {
  switch (action.type) {
    case "load/start":
      return { ...state, status: "loading", error: null };
    case "load/success": {
      const { sessions, tracks, rooms, speakers, conference } = action.payload;
      return { ...state, status: "ready", error: null, sessions, tracks, rooms, speakers, conference };
    }
    case "load/error":
      return { ...state, status: "error", error: action.error };
    case "day/select":
      return { ...state, selectedDay: action.day };
    case "favorite/toggle": {
      // Copy-on-write the Set so subscribers see a fresh identity: persistence keys
      // off `favorites !== prev`, so mutating in place would skip the save.
      const favorites = new Set(state.favorites);
      if (favorites.has(action.id)) {
        favorites.delete(action.id);
      } else {
        favorites.add(action.id);
      }
      return { ...state, favorites };
    }
    case "favorites/hydrate":
      // ignore non-integers: the ids come from untrusted localStorage.
      return { ...state, favorites: new Set(action.ids.filter((n) => Number.isInteger(n))) };
    case "filters/set":
      return { ...state, filters: action.filters };
    case "filters/clear":
      return { ...state, filters: emptyFilters };
    default:
      return state;
  }
}

export const store = createStore(initialState, reducer);

if (import.meta.env.DEV) {
  // TEST-ONLY: expose the NET count of live store subscriptions so the e2e can assert
  // view teardown (a leaked view keeps its subscription after navigating away). This
  // wraps `subscribe` at module-eval time, before any view or persistence subscribes,
  // so every subscription flows through the counter. import.meta.env.DEV is false under
  // `vite build`, so the block is dead-code-eliminated from the shipped bundle.
  let liveSubscriptions = 0;
  const baseSubscribe = store.subscribe;
  store.subscribe = (listener) => {
    liveSubscriptions += 1;
    const unsubscribe = baseSubscribe(listener);
    return () => {
      liveSubscriptions -= 1;
      unsubscribe();
    };
  };
   (globalThis).__subscriberCount = () => liveSubscriptions;

  // Wrap dispatch here at module eval, before main.js kicks the first
  // favorites/hydrate / load/start, so the very first actions are logged too.
  attachStoreDevtools(store);
}
